/**
 * TemplateFieldsRenderer.js
 *
 * Reusable component to render custom template fields in phase components.
 * Used by StartVoyageFullScreen, PreArrivalPhase, and ArrivalPhase to render
 * any custom fields added via the template editor in Voyage Settings.
 *
 * Field types supported:
 * - select: Button grid (2 columns)
 * - radio: Button grid (2 columns)
 * - number: Number input
 * - text: Text input
 * - time: Time input
 */

import React from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * TemplateFieldsRenderer
 *
 * @param {array} fields - Array of field definitions from template
 * @param {object} values - Current field values { fieldId: value }
 * @param {function} onChange - Callback when a field value changes (fieldId, value)
 * @param {object} errors - Optional errors object { fieldId: errorMessage }
 * @param {string} title - Optional section title (default: "Additional Information")
 */
const TemplateFieldsRenderer = ({
  fields = [],
  values = {},
  onChange,
  errors = {},
  title = 'Additional Information'
}) => {
  // Don't render anything if no fields
  if (!fields || fields.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Section Header */}
      <div className="px-4 py-3 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">{title}</h3>
      </div>

      {/* Fields */}
      <div className="p-4 space-y-4">
        {fields.map(field => (
          <div key={field.id}>
            {/* Label */}
            <label
              htmlFor={field.id}
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>

            {/* Input based on field type */}
            {field.type === 'select' ? (
              <div className="space-y-2">
                {/* Button Grid for Select Options - mobile-friendly */}
                <div className="grid grid-cols-2 gap-2">
                  {(field.options || []).map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => onChange(field.id, opt.value)}
                      className={`
                        px-4 py-3 min-h-[56px] rounded-lg text-sm font-medium transition-colors
                        border-2
                        ${values[field.id] === opt.value
                          ? 'bg-cyan-50 border-cyan-500 text-cyan-700 ring-2 ring-cyan-300'
                          : errors[field.id]
                            ? 'bg-white border-red-300 text-slate-700 hover:border-red-400'
                            : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                        }
                      `}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {!values[field.id] && (
                  <p className="text-xs text-slate-500">Tap to select {field.label.toLowerCase()}</p>
                )}
              </div>
            ) : field.type === 'radio' ? (
              <div className="grid grid-cols-2 gap-2">
                {(field.options || []).map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onChange(field.id, opt.value)}
                    className={`
                      px-4 py-3 min-h-[56px] rounded-lg text-sm font-medium transition-colors
                      border-2
                      ${values[field.id] === opt.value
                        ? 'bg-cyan-50 border-cyan-500 text-cyan-700 ring-2 ring-cyan-300'
                        : errors[field.id]
                          ? 'bg-white border-red-300 text-slate-700 hover:border-red-400'
                          : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                      }
                    `}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            ) : field.type === 'number' ? (
              <input
                type="number"
                id={field.id}
                value={values[field.id] || ''}
                onChange={(e) => onChange(field.id, e.target.value)}
                placeholder={field.placeholder || ''}
                min="0"
                className={`
                  w-full px-3 py-3 min-h-[56px] text-base border rounded-lg transition-colors
                  focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500
                  ${errors[field.id] ? 'border-red-300 bg-red-50' : 'border-slate-300'}
                `}
              />
            ) : field.type === 'time' ? (
              <input
                type="time"
                id={field.id}
                value={values[field.id] || ''}
                onChange={(e) => onChange(field.id, e.target.value)}
                className={`
                  w-full px-3 py-3 min-h-[56px] text-base border rounded-lg transition-colors
                  focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500
                  ${errors[field.id] ? 'border-red-300 bg-red-50' : 'border-slate-300'}
                `}
              />
            ) : (
              // Default to text input
              <input
                type="text"
                id={field.id}
                value={values[field.id] || ''}
                onChange={(e) => onChange(field.id, e.target.value)}
                placeholder={field.placeholder || ''}
                className={`
                  w-full px-3 py-3 min-h-[56px] text-base border rounded-lg transition-colors
                  focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500
                  ${errors[field.id] ? 'border-red-300 bg-red-50' : 'border-slate-300'}
                `}
              />
            )}

            {/* Error message */}
            {errors[field.id] && (
              <div className="flex items-center gap-1 mt-1 text-sm text-red-600">
                <AlertCircle className="h-3 w-3" />
                <span>{errors[field.id]}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TemplateFieldsRenderer;
